import { ZuiBaseElement } from '@zywave/zui-base';
import { html, PropertyValues } from 'lit';
import { property, query, queryAssignedNodes } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { illustrationsJsImportMap } from './illustrations-js/illustrations-js-import-map.js';
import { style } from './zui-error-page-css.js';
import type { SVGTemplateResult } from 'lit';

/**
 * Standardized way of showing common errors to users.
 *
 * @slot - Default, unnamed slot; for passing additional information to be rendered beneath the default rendered content of `<zui-error-page>`
 */
export class ZuiErrorPage extends ZuiBaseElement {
  /**
   * Provide an error type to deteremine which SVG to render
   * @type { '401' | '403' | '404' | '500' | null }
   */
  @property({ type: String, attribute: 'error' })
  error: ZuiErrorPageError = null;

  /**
   * Pass header text into component
   */
  @property({ type: String, attribute: 'header' })
  header: string | null = null;

  /**
   * Pass subheader text into component
   */
  @property({ type: String, attribute: 'subheader' })
  subheader: string | null = null;

  /**
   * Pass body message text into component
   */
  @property({ type: String, attribute: 'message' })
  message: string | null = null;

  @query('.slot-wrapper')
  private _slotWrapper: HTMLDivElement;

  @queryAssignedNodes({ slot: '' })
  private _slottedNodes: Array<Node>;

  static get styles() {
    return [super.styles, style];
  }

  #illustration: SVGTemplateResult | undefined;

  async #loadIllustration() {
    const importedIllustrationFunc = illustrationsJsImportMap[this.error];
    if (typeof importedIllustrationFunc === 'function') {
      const importedIllustrationModule = await importedIllustrationFunc();
      const importedIllustrationSvg = importedIllustrationModule.svgSprite;
      this.#illustration = importedIllustrationSvg;
      this.requestUpdate();
      await this.updateComplete;
      const illustrationLoadEvent = new CustomEvent('svg-load');
      this.dispatchEvent(illustrationLoadEvent);
    }
  }

  updated(changedProps: PropertyValues) {
    if (changedProps.has('error')) {
      this.#loadIllustration();
    }
  }

  firstUpdated() {
    this.#onSlotChange();
  }

  render() {
    return html`<div class="container">
      ${this.#illustration ? this.#illustration : html``}
      ${this.header ? html`<h1 class="header">${this.header}</h1>` : html``}
      ${this.subheader ? html`<h2 class="subheader">${this.subheader}</h2>` : html``}
      ${this.message ? html`<p class="message">${unsafeHTML(this.message)}</p>` : html``}
      <div class="slot-wrapper"><slot @slotchange="${this.#onSlotChange}"></slot></div>
    </div>`;
  }

  async #onSlotChange() {
    const slotted = await this._slottedNodes;
    if (slotted.length > 0) {
      this._slotWrapper.classList.toggle('show', true);
    } else {
      this._slotWrapper.classList.toggle('show', false);
    }
    this.requestUpdate();
  }
}

window.customElements.define('zui-error-page', ZuiErrorPage);

export type ZuiErrorPageError = '401' | '403' | '404' | '500' | null;

declare global {
  interface HTMLElementTagNameMap {
    'zui-error-page': ZuiErrorPage;
  }
}
